/**
 * Base Service Class
 * Provides common functionality for all API services
 */

import { makeAuthenticatedRequest } from './backendApiService';
import { convertUTCDatesToLocal, convertLocalDatesToUTC } from '../utils/dateUtils';

export class BaseService {
    constructor(baseUrl, dateFields = ['createdAt', 'updatedAt', 'lastModifiedAt']) {
        this.baseUrl = baseUrl;
        this.dateFields = dateFields;
    }

    /**
     * Convert dates in response data from UTC to local
     * @param {Object|Array} data - Response data
     * @returns {Object|Array} Data with converted dates
     */
    convertDates(data) {
        if (!data || this.dateFields.length === 0) return data;
        return convertUTCDatesToLocal(data, this.dateFields);
    }

    /**
     * Convert dates in request data from local to UTC
     * @param {Object|Array} data - Request data
     * @returns {Object|Array} Data with dates converted to UTC ISO strings
     */
    convertDatesToUTC(data) {
        if (!data || this.dateFields.length === 0) return data;
        return convertLocalDatesToUTC(data, this.dateFields);
    }

    /**
     * Make an authenticated request to the API
     * @param {string} accessToken - JWT access token
     * @param {string} endpoint - API endpoint (relative to baseUrl)
     * @param {object} options - Fetch options
     * @returns {Promise} Response data with converted dates
     */
    async makeRequest(accessToken, endpoint = '', options = {}) {
        const response = await makeAuthenticatedRequest(accessToken, `${this.baseUrl}${endpoint}`, options);
        return this.convertDates(response);
    }

    /**
     * Common validation for required fields
     * @param {object} data - Data to validate
     * @param {string[]} requiredFields - Array of required field names
     */
    validateRequiredFields(data, requiredFields) {
        const missingFields = requiredFields.filter(field => !data[field]);
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }
    }

    /**
     * Common validation for field length
     * @param {object} data - Data to validate
     * @param {object} fieldLimits - Object with field names as keys and max lengths as values
     */
    validateFieldLengths(data, fieldLimits) {
        for (const [field, maxLength] of Object.entries(fieldLimits)) {
            if (data[field] && data[field].length > maxLength) {
                throw new Error(`${field} cannot exceed ${maxLength} characters`);
            }
        }
    }

    /**
     * Standard CRUD operations
     */

    /**
     * Get all records
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of records
     */
    async getAll(accessToken) {
        try {
            const response = await this.makeRequest(accessToken);
            return response;
        } catch (error) {
            console.error(`Failed to fetch all records from ${this.baseUrl}:`, error);
            throw error;
        }
    }

    /**
     * Get a specific record by ID
     * @param {string} accessToken - JWT access token
     * @param {number} id - Record ID
     * @returns {Promise<Object>} Record object
     */
    async getById(accessToken, id) {
        try {
            const response = await this.makeRequest(accessToken, `/${id}`);
            return response;
        } catch (error) {
            console.error(`Failed to fetch record ${id} from ${this.baseUrl}:`, error);
            throw error;
        }
    }

    /**
     * Create a new record
     * @param {string} accessToken - JWT access token
     * @param {Object} data - Record data
     * @returns {Promise<Object>} Created record object
     */
    async create(accessToken, data) {
        try {
            // Convert any Date objects or datetime-local strings to UTC before sending
            const utcData = this.convertDatesToUTC(data);
            
            const response = await this.makeRequest(accessToken, '', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(utcData)
            });
            return response;
        } catch (error) {
            console.error(`Failed to create record in ${this.baseUrl}:`, error);
            throw error;
        }
    }

    /**
     * Update an existing record
     * @param {string} accessToken - JWT access token
     * @param {number} id - Record ID
     * @param {Object} data - Updated record data
     * @returns {Promise<Object>} Updated record object
     */
    async update(accessToken, id, data) {
        try {
            // Convert any Date objects or datetime-local strings to UTC before sending
            const utcData = this.convertDatesToUTC(data);
            
            const response = await this.makeRequest(accessToken, `/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(utcData)
            });
            return response;
        } catch (error) {
            console.error(`Failed to update record ${id} in ${this.baseUrl}:`, error);
            throw error;
        }
    }

    /**
     * Delete a record
     * @param {string} accessToken - JWT access token
     * @param {number} id - Record ID
     * @returns {Promise<void>}
     */
    async delete(accessToken, id) {
        try {
            await this.makeRequest(accessToken, `/${id}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to delete record ${id} from ${this.baseUrl}:`, error);
            throw error;
        }
    }

    /**
     * Check if a record exists
     * @param {string} accessToken - JWT access token
     * @param {string} endpoint - Endpoint to check
     * @returns {Promise<boolean>} True if exists, false otherwise
     */
    async exists(accessToken, endpoint) {
        try {
            const response = await this.makeRequest(accessToken, endpoint);
            return response;
        } catch (error) {
            console.error(`Failed to check existence at ${this.baseUrl}${endpoint}:`, error);
            throw error;
        }
    }

    /**
     * Search records
     * @param {string} accessToken - JWT access token
     * @param {string} searchTerm - Search term
     * @param {string} searchField - Field to search (default: 'name')
     * @returns {Promise<Array>} Array of matching records
     */
    async search(accessToken, searchTerm, searchField = 'name') {
        try {
            const response = await this.makeRequest(
                accessToken, 
                `/search?${searchField}=${encodeURIComponent(searchTerm)}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to search records in ${this.baseUrl}:`, error);
            throw error;
        }
    }
}

export default BaseService;